import { AlertTriangle, ArrowUpRight, Banknote, ChevronRight, House, Plane, Wallet } from "lucide-react";
import type { Account, DashboardData } from "../../types";
import { accountBalanceAtMonthEnd } from "../../lib/account-balances";
import { cardStatementStatus, isCard, money, shortName, todayIn } from "../../lib/finance";
import { getAccountTrip } from "../../lib/trips";
import { accountGoalBreakdown } from "../../lib/goal-reserves";
import AccountAllocationBar from "../../components/AccountAllocationBar";
import { Private, usePrivacy } from "../../components/Privacy";

export default function AccountCard({ account: a, data, month, historical, isLeftover = false, isSettled = false, onOpenAccount, onOpenTrip }: {
  account: Account;
  data: DashboardData;
  month: string;
  historical: boolean;
  isLeftover?: boolean;
  isSettled?: boolean;
  onOpenAccount: (account: Account) => void;
  onOpenTrip?: (id: string) => void;
}) {
  const { hidden } = usePrivacy();
  const balance = accountBalanceAtMonthEnd(a, data.transactions, month);
  const statement = isCard(a) && !historical
    ? cardStatementStatus(a, data.transactions || [], todayIn(data.timezone))
    : null;
  const trip = getAccountTrip(a, data.trips);
  const isCash = a.type.toLowerCase() === "cash";
  const isTripWallet = isCash && Boolean(trip);
  const accountGoals = !isCard(a)
    ? accountGoalBreakdown(data.goals, a.id, month, data.transactions, data.accounts)
    : [];
  const reserved = accountGoals.reduce((sum, item) => sum + item.reserved, 0);
  const cushion = accountGoals.reduce((max, item) => Math.max(max, item.cushionAmount), 0);

  return (
    <button style={{ width: "100%", height: "100%" }}
      className={
        "account-card " +
        (isCard(a) ? "credit-account " : "") +
        (isLeftover ? "leftover-account-card " : "") +
        (isSettled ? "settled-account-card " : "")
      }
      onClick={() => onOpenAccount(a)}
    >
      <div className="account-card-top">
        <span
          className={
            "account-type-icon " +
            (isTripWallet ? "trip-account-icon" : "")
          }
        >
          {isCard(a) ? (
            <Wallet size={20} />
          ) : isTripWallet ? (
            <Plane size={18} />
          ) : isCash ? (
            <Banknote size={19} />
          ) : (
            <House size={20} />
          )}
        </span>
        <span>
          {a.currency} <span>·</span> {a.type}
        </span>
        <ArrowUpRight size={19} />
      </div>
      <h3>{shortName(a.name)}</h3>
      {trip && (
        <span
          role="button"
          tabIndex={0}
          className="account-trip-badge clickable"
          onClick={(e) => {
            e.stopPropagation();
            onOpenTrip?.(trip.id);
          }}
          onKeyDown={(e) => {
            if (e.key === "Enter" || e.key === " ") {
              e.stopPropagation();
              e.preventDefault();
              onOpenTrip?.(trip.id);
            }
          }}
          title={`View trip: ${trip.name}`}
        >
          <Plane size={11} />
          <span>{trip.name}</span>
          {trip.status === "completed" && (
            <span className="account-trip-status">Completed</span>
          )}
          <ChevronRight size={10} className="account-trip-arrow" />
        </span>
      )}
      {statement && statement.state !== "current" && (
        <span className="statement-warning">
          <AlertTriangle size={14} />
          {statement.state === "stale"
            ? "Update statement details"
            : "Statement details needed"}
        </span>
      )}
      <div
        className={
          "account-balance " +
          (!hidden &&
          (isCard(a) && a.creditLimit != null
            ? a.creditLimit - balance
            : balance) < 0
            ? "negative"
            : "")
        }
      >
        <Private>
          {money(
            isCard(a) && a.creditLimit != null
              ? a.creditLimit - balance
              : balance,
            a.currency,
          ).replace(" " + a.currency, "")}
        </Private>
        <span>{a.currency}</span>
      </div>
      {!isCard(a) && accountGoals.length > 0 && (
        <AccountAllocationBar
          balance={balance}
          reserved={reserved}
          cushion={cushion}
          currency={a.currency}
          variant="card"
          availableLabel={historical ? "Unallocated at month end" : undefined}
          unfundedLabel={
            accountGoals.length === 1
              ? accountGoals[0].goal.name
              : `${accountGoals.length} linked goals`
          }
          unfundedTarget={accountGoals.reduce((sum, item) => sum + item.goal.targetAmount, 0)}
        />
      )}
      {isCard(a) ? (
        <>
          <div className="credit-meta">
            <span>
              {a.creditLimit != null ? "Available credit" : "Amount owed"}
            </span>
            <strong>
              {a.creditLimit
                ? (
                  <Private>{`${Math.round((Math.max(0, balance) / a.creditLimit) * 100)}% used`}</Private>
                )
                : "No limit set"}
            </strong>
          </div>
          {a.creditLimit && (
            <>
              {!hidden && (
                <div className="credit-track">
                  <span
                    style={{
                      width: `${Math.min(100, (Math.max(0, balance) / a.creditLimit) * 100)}%`,
                    }}
                  />
                </div>
              )}
              <div className="account-card-foot">
                <span>
                  <Private>{money(Math.max(0, balance), a.currency)}</Private> owed
                </span>
                <span>
                  Limit <Private>{money(a.creditLimit, a.currency, 0)}</Private>
                </span>
              </div>
            </>
          )}{" "}
        </>
      ) : isLeftover && !historical ? (
        <div className="account-card-foot">
          <span>Leftover trip cash</span>
          <span>
            Resolve or roll over <ChevronRight size={14} />
          </span>
        </div>
      ) : (
        <div className="account-card-foot">
          <span>{historical ? "Closing balance" : "Available balance"}</span>
          <span>
            View activity <ChevronRight size={14} />
          </span>
        </div>
      )}
    </button>
  );
}
